import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { selectActiveTitle } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle as selectActiveTitleV2 } from '@/redux/selectors/titleV2Selectors';
import { selectSelectedVersionId } from '@/redux/selectors/orderPlacementSelectors';
import { selectPackagingActiveTemplate } from '@/redux/selectors/packagingSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { convertStylesToStyled } from '@/_utils/utils';
import { buildTitleURL, getTitlePath } from '@/_utils/titleUtils';
import Poster from '@/components/_common/Poster';
import LinkButton from '@/components/_common/LinkButton';
import PolarisIcon from '@/components/_common/PolarisIcon';

const OPViewingTitleHeader = () => {
  const viewingTitleHeader = useConfigValue(['orderPlacementConfig', 'viewingTitleHeader']);
  const isTitleV2 = useConfigValue(['titleConfig', 'v2']);
  const activeTitle = isTitleV2 ? useAppSelector(selectActiveTitleV2) : useAppSelector(selectActiveTitle);
  const selectedVersionId = useAppSelector(selectSelectedVersionId);
  const activePackagingTemplate = useAppSelector(selectPackagingActiveTemplate);

  if (!activeTitle) return null;

  const { id, thumbnail_link } = activeTitle;

  let titleUrl: string;
  if (isTitleV2) {
    titleUrl = buildTitleURL({
      path: getTitlePath(activeTitle),
      version: selectedVersionId ?? undefined
    });
  } else {
    titleUrl = selectedVersionId !== null ? `/titles/${id}/?tab=metadata&version_id=${selectedVersionId}` : `/titles/${id}`;
  }

  return (
    <div>
      <Wrapper>
        <Poster src={thumbnail_link || ''} maxWidth={75} display={{ xs: 'none', lg: 'flex' }} />
        <Box ml={2} width="100%">
          {viewingTitleHeader ? viewingTitleHeader(activeTitle) : <Typography variant="h5">{activeTitle.name}</Typography>}
          {!activePackagingTemplate && (
            <TitleDetailsLink href={titleUrl} color="primary" target="_blank" endIcon={<PolarisIcon icon="external_link" height={18} />}>
              View Title Details
            </TitleDetailsLink>
          )}
        </Box>
      </Wrapper>
    </div>
  );
};

export default OPViewingTitleHeader;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255,255,255,.5)')};
  flex-direction: column;

  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TitleArea)}

  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex-direction: row;
  }

  & label {
    padding-left: 0;
  }
`;

const TitleDetailsLink = styled(LinkButton)(() => ({
  '&:hover': {
    '& svg': {
      opacity: 1
    }
  },
  '& svg': {
    transition: 'opacity 200ms ease-in-out',
    opacity: 0
  }
}));
